import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import { hasWorkforceBreakdownData } from "@/lib/company-data-availability";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { GlobeIcon, UsersIcon } from "lucide-react";

type BreakdownItem = {
  labelKey: string;
  value: number;
};

function HorizontalBar({
  items,
  t,
}: {
  items: BreakdownItem[];
  t: (key: string) => string;
}) {
  const maxValue = Math.max(...items.map((i) => i.value));

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <div key={item.labelKey} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-24 text-right shrink-0 truncate">
            {t(item.labelKey)}
          </span>
          <div className="flex-1 h-5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary/70 transition-all duration-500"
              style={{
                width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%`,
              }}
            />
          </div>
          <span className="text-xs font-semibold w-8 text-right shrink-0">
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

type CountryItem = {
  country: string;
  count: number;
};

/** ISO country code → flag emoji; falls back to globe icon on failure. */
function countryToFlag(country: string): string | null {
  const trimmed = country.trim();
  if (trimmed.length !== 2) return null;
  const upper = trimmed.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return null;
  const base = 0x1f1e6;
  const codePoints = [
    base + (upper.charCodeAt(0) - 65),
    base + (upper.charCodeAt(1) - 65),
  ];
  return String.fromCodePoint(...codePoints);
}

function CountryBar({ items }: { items: CountryItem[] }) {
  const total = items.reduce((acc, item) => acc + item.count, 0);
  const maxValue = Math.max(...items.map((i) => i.count));

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => {
        const flag = countryToFlag(item.country);
        const percentage = total > 0 ? (item.count / total) * 100 : 0;
        return (
          <div key={item.country} className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 w-20 text-right shrink-0 justify-end">
              {flag ? (
                <span className="text-base leading-none">{flag}</span>
              ) : (
                <GlobeIcon className="w-3.5 h-3.5 text-muted-foreground" />
              )}
              <span className="text-xs font-medium truncate">
                {item.country}
              </span>
            </span>
            <div className="flex-1 h-5 rounded-full bg-muted overflow-hidden relative min-w-0">
              <div
                className="h-full rounded-full bg-gradient-to-r from-sky-500/80 to-indigo-500/80 transition-all duration-500"
                style={{
                  width: `${maxValue > 0 ? (item.count / maxValue) * 100 : 0}%`,
                }}
              />
            </div>
            <span className="text-xs font-semibold w-12 text-right shrink-0 tabular-nums">
              {item.count}
              <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                {percentage.toFixed(0)}%
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

type CardWorkforceBreakdownProps = {
  company: CompanyDTO;
};

export default function CardWorkforceBreakdown({
  company,
}: CardWorkforceBreakdownProps) {
  const { t } = useTranslation();

  if (!hasWorkforceBreakdownData(company)) return null;

  const departmental = company.departmentalHeadcount;
  const seniority = company.seniorityBreakdown;

  const departmentItems: BreakdownItem[] = departmental
    ? ([
        { labelKey: "company.signals.workforce.sales", value: departmental.sales },
        { labelKey: "company.signals.workforce.technical", value: departmental.technical },
        { labelKey: "company.signals.workforce.marketing", value: departmental.marketing },
        { labelKey: "company.signals.workforce.operations", value: departmental.operations },
        { labelKey: "company.signals.workforce.hr", value: departmental.hr },
        { labelKey: "company.signals.workforce.finance", value: departmental.finance },
        { labelKey: "company.signals.workforce.legal", value: departmental.legal },
        { labelKey: "company.signals.workforce.consulting", value: departmental.consulting },
        { labelKey: "company.signals.workforce.product", value: departmental.product },
        { labelKey: "company.signals.workforce.medical", value: departmental.medical },
        { labelKey: "company.signals.workforce.generalManagement", value: departmental.generalManagement },
        { labelKey: "company.signals.workforce.administrative", value: departmental.administrative },
        { labelKey: "company.signals.workforce.customerService", value: departmental.customerService },
        { labelKey: "company.signals.workforce.design", value: departmental.design },
        { labelKey: "company.signals.workforce.education", value: departmental.education },
        { labelKey: "company.signals.workforce.projectManagement", value: departmental.projectManagement },
        { labelKey: "company.signals.workforce.realEstate", value: departmental.realEstate },
        { labelKey: "company.signals.workforce.research", value: departmental.research },
        { labelKey: "company.signals.workforce.trades", value: departmental.trades },
        { labelKey: "company.signals.workforce.other", value: departmental.other },
      ] as { labelKey: string; value: number | null }[])
        .filter((item): item is BreakdownItem => item.value != null && item.value > 0)
        .sort((a, b) => b.value - a.value)
    : [];

  const seniorityItems: BreakdownItem[] = seniority
    ? ([
        { labelKey: "company.signals.workforce.cLevel", value: seniority.cLevel },
        { labelKey: "company.signals.workforce.vp", value: seniority.vp },
        { labelKey: "company.signals.workforce.director", value: seniority.director },
        { labelKey: "company.signals.workforce.head", value: seniority.head },
        { labelKey: "company.signals.workforce.manager", value: seniority.manager },
        { labelKey: "company.signals.workforce.senior", value: seniority.senior },
        { labelKey: "company.signals.workforce.founder", value: seniority.founder },
        { labelKey: "company.signals.workforce.partner", value: seniority.partner },
        { labelKey: "company.signals.workforce.owner", value: seniority.owner },
        { labelKey: "company.signals.workforce.intern", value: seniority.intern },
        { labelKey: "company.signals.workforce.specialist", value: seniority.specialist },
        { labelKey: "company.signals.workforce.otherManagement", value: seniority.otherManagement },
      ] as { labelKey: string; value: number | null }[])
        .filter((item): item is BreakdownItem => item.value != null && item.value > 0)
        .sort((a, b) => b.value - a.value)
    : [];

  const countryItems: CountryItem[] = (company.employeesCount?.byCountry ?? [])
    .filter((item) => item.country && item.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const sectionCount =
    (departmentItems.length > 0 ? 1 : 0) +
    (seniorityItems.length > 0 ? 1 : 0) +
    (countryItems.length > 0 ? 1 : 0);

  if (sectionCount === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3 sm:pb-4">
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-muted">
            <UsersIcon className="w-5 h-5" />
          </div>
          {t("company.signals.workforce.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "grid gap-6",
            sectionCount === 3
              ? "grid-cols-1 @3xl:grid-cols-3"
              : sectionCount === 2
                ? "grid-cols-1 @3xl:grid-cols-2"
                : "grid-cols-1",
          )}
        >
          {departmentItems.length > 0 && (
            <div className="flex flex-col gap-3">
              <h4 className="text-sm font-semibold text-muted-foreground">
                {t("company.signals.workforce.byDepartment")}
              </h4>
              <HorizontalBar items={departmentItems} t={t} />
            </div>
          )}

          {seniorityItems.length > 0 && (
            <div className="flex flex-col gap-3">
              <h4 className="text-sm font-semibold text-muted-foreground">
                {t("company.signals.workforce.bySeniority")}
              </h4>
              <HorizontalBar items={seniorityItems} t={t} />
            </div>
          )}

          {countryItems.length > 0 && (
            <div className="flex flex-col gap-3">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <GlobeIcon className="w-4 h-4" />
                {t("company.countries.title")}
              </h4>
              <CountryBar items={countryItems} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
